// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator } from "@azure/core-paging";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import type {
  FleetspaceAccountResource,
  FleetspaceAccountListOptionalParams,
  FleetspaceAccountGetOptionalParams,
  FleetspaceAccountGetResponse,
  FleetspaceAccountCreateOptionalParams,
  FleetspaceAccountCreateResponse,
  FleetspaceAccountDeleteOptionalParams,
  FleetspaceAccountDeleteResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Interface representing a FleetspaceAccount. */
export interface FleetspaceAccount {
  /**
   * Lists all the fleetspaces accounts  under a fleetspace.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param options The options parameters.
   */
  list(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    options?: FleetspaceAccountListOptionalParams,
  ): PagedAsyncIterableIterator<FleetspaceAccountResource>;
  /**
   * Retrieves the properties of an existing Azure Cosmos DB fleetspace account under a fleetspace
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param fleetspaceAccountName Cosmos DB fleetspace account name.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    fleetspaceAccountName: string,
    options?: FleetspaceAccountGetOptionalParams,
  ): Promise<FleetspaceAccountGetResponse>;
  /**
   * Creates an Azure Cosmos DB fleetspace account under a fleetspace.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param fleetspaceAccountName Cosmos DB fleetspace account name.
   * @param body The parameters to provide for the current fleetspace account.
   * @param options The options parameters.
   */
  beginCreate(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    fleetspaceAccountName: string,
    body: FleetspaceAccountResource,
    options?: FleetspaceAccountCreateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<FleetspaceAccountCreateResponse>,
      FleetspaceAccountCreateResponse
    >
  >;
  /**
   * Creates an Azure Cosmos DB fleetspace account under a fleetspace.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param fleetspaceAccountName Cosmos DB fleetspace account name.
   * @param body The parameters to provide for the current fleetspace account.
   * @param options The options parameters.
   */
  beginCreateAndWait(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    fleetspaceAccountName: string,
    body: FleetspaceAccountResource,
    options?: FleetspaceAccountCreateOptionalParams,
  ): Promise<FleetspaceAccountCreateResponse>;
  /**
   * Removes an existing Azure Cosmos DB fleetspace account from a fleetspace.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param fleetspaceAccountName Cosmos DB fleetspace account name.
   * @param options The options parameters.
   */
  beginDelete(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    fleetspaceAccountName: string,
    options?: FleetspaceAccountDeleteOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<FleetspaceAccountDeleteResponse>,
      FleetspaceAccountDeleteResponse
    >
  >;
  /**
   * Removes an existing Azure Cosmos DB fleetspace account from a fleetspace.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param fleetspaceAccountName Cosmos DB fleetspace account name.
   * @param options The options parameters.
   */
  beginDeleteAndWait(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    fleetspaceAccountName: string,
    options?: FleetspaceAccountDeleteOptionalParams,
  ): Promise<FleetspaceAccountDeleteResponse>;
}
